export enum WorkOptions {
    NotSet = 0,
    IT = 1,
    AdministrativeWork = 2,
    Banking = 3,
    Security = 4,
    Accounting = 5,
    CivilService = 6,
    Design = 7,
    DomesticStaff = 8,
    HousingAndUtilities = 9,
    Procurement = 10,
    Arts = 11,
    HR = 12,
    Consulting = 13,
    Marketing = 14,
    Medicine = 15,
    Science = 16,
    NonProfitOrganizations = 17,
    Sales = 18,
    Industry = 19,
    BlueCollarWorkers = 20,
    Media = 21,
    Agriculture = 22,
    DeliveryServices = 23,
    SocialServices = 24,
    Sports = 25,
    Insurance = 26,
    Construction = 27,
    RawMaterials = 28,
    Transport = 29,
    Tourism = 30,
    Services = 31,
    Ecology = 32,
    Energy = 33,
    Law = 34,
    TechnicalOrganization = 35,
}

export type WorkOptionItem = {
    label: string;
    value: WorkOptions;
};

type WorkOptionLabels = {
    ru: string;
    en: string;
};

/** Подписи из summary enum (ru / en). */
const WORK_OPTION_LABELS: Record<WorkOptions, WorkOptionLabels> = {
    [WorkOptions.NotSet]: { ru: 'Не задано', en: 'Not set' },
    [WorkOptions.IT]: {
        ru: 'IT, Интернет, связь, телеком',
        en: 'IT, Internet, Communications, Telecom',
    },
    [WorkOptions.AdministrativeWork]: {
        ru: 'Административная работа, секретариат, АХО',
        en: 'Administrative Work, Secretariat, Office Management',
    },
    [WorkOptions.Banking]: {
        ru: 'Банки, инвестиции, лизинг',
        en: 'Banking, Investments, Leasing',
    },
    [WorkOptions.Security]: {
        ru: 'Безопасность, службы охраны',
        en: 'Security, Guard Services',
    },
    [WorkOptions.Accounting]: {
        ru: 'Бухгалтерия, финансы, аудит',
        en: 'Accounting, Finance, Audit',
    },
    [WorkOptions.CivilService]: {
        ru: 'Государственная служба',
        en: 'Civil Service',
    },
    [WorkOptions.Design]: {
        ru: 'Дизайн, творчество',
        en: 'Design, Creative Arts',
    },
    [WorkOptions.DomesticStaff]: {
        ru: 'Домашний персонал',
        en: 'Domestic Staff',
    },
    [WorkOptions.HousingAndUtilities]: {
        ru: 'ЖКХ',
        en: 'Housing and Utilities',
    },
    [WorkOptions.Procurement]: {
        ru: 'Закупки, снабжение',
        en: 'Procurement, Supply',
    },
    [WorkOptions.Arts]: {
        ru: 'Искусство, культура, развлечения',
        en: 'Arts, Culture, Entertainment',
    },
    [WorkOptions.HR]: {
        ru: 'Кадры, управление персоналом',
        en: 'HR, Personnel Management',
    },
    [WorkOptions.Consulting]: {
        ru: 'Консалтинг, стратегическое развитие',
        en: 'Consulting, Strategic Development',
    },
    [WorkOptions.Marketing]: {
        ru: 'Маркетинг, реклама, PR',
        en: 'Marketing, Advertising, PR',
    },
    [WorkOptions.Medicine]: {
        ru: 'Медицина, фармацевтика, ветеринария',
        en: 'Medicine, Pharmaceuticals, Veterinary',
    },
    [WorkOptions.Science]: {
        ru: 'Наука, образование, повышение квалификации',
        en: 'Science, Education, Professional Development',
    },
    [WorkOptions.NonProfitOrganizations]: {
        ru: 'Некоммерческие организации, волонтерство',
        en: 'Non-Profit Organizations, Volunteering',
    },
    [WorkOptions.Sales]: { ru: 'Продажи', en: 'Sales' },
    [WorkOptions.Industry]: {
        ru: 'Промышленность, производство',
        en: 'Industry, Manufacturing',
    },
    [WorkOptions.BlueCollarWorkers]: {
        ru: 'Рабочий персонал',
        en: 'Blue-Collar Workers',
    },
    [WorkOptions.Media]: {
        ru: 'СМИ, издательства',
        en: 'Media, Publishing',
    },
    [WorkOptions.Agriculture]: {
        ru: 'Сельское хозяйство',
        en: 'Agriculture',
    },
    [WorkOptions.DeliveryServices]: {
        ru: 'Службы доставки',
        en: 'Delivery Services',
    },
    [WorkOptions.SocialServices]: {
        ru: 'Социальные службы',
        en: 'Social Services',
    },
    [WorkOptions.Sports]: {
        ru: 'Спорт, фитнес, салоны красоты, SPA',
        en: 'Sports, Fitness, Beauty Salons, SPA',
    },
    [WorkOptions.Insurance]: { ru: 'Страхование', en: 'Insurance' },
    [WorkOptions.Construction]: {
        ru: 'Строительство, проектирование, недвижимость',
        en: 'Construction, Design, Real Estate',
    },
    [WorkOptions.RawMaterials]: { ru: 'Сырье', en: 'Raw Materials' },
    [WorkOptions.Transport]: {
        ru: 'Транспорт, логистика, ВЭД',
        en: 'Transport, Logistics, Foreign Trade',
    },
    [WorkOptions.Tourism]: {
        ru: 'Туризм, гостиницы, общественное питание',
        en: 'Tourism, Hotels, Catering',
    },
    [WorkOptions.Services]: {
        ru: 'Услуги, ремонт, сервисное обслуживание',
        en: 'Services, Repair, Maintenance',
    },
    [WorkOptions.Ecology]: { ru: 'Экология', en: 'Ecology' },
    [WorkOptions.Energy]: { ru: 'Энергетика', en: 'Energy' },
    [WorkOptions.Law]: { ru: 'Юриспруденция', en: 'Law, Legal Services' },
    [WorkOptions.TechnicalOrganization]: {
        ru: 'Техническая организация',
        en: 'Technical organization',
    },
};

/** Значения enum в порядке объявления (без NotSet — для селекта). */
const WORK_OPTIONS_VALUES: WorkOptions[] = [
    WorkOptions.IT,
    WorkOptions.AdministrativeWork,
    WorkOptions.Banking,
    WorkOptions.Security,
    WorkOptions.Accounting,
    WorkOptions.CivilService,
    WorkOptions.Design,
    WorkOptions.DomesticStaff,
    WorkOptions.HousingAndUtilities,
    WorkOptions.Procurement,
    WorkOptions.Arts,
    WorkOptions.HR,
    WorkOptions.Consulting,
    WorkOptions.Marketing,
    WorkOptions.Medicine,
    WorkOptions.Science,
    WorkOptions.NonProfitOrganizations,
    WorkOptions.Sales,
    WorkOptions.Industry,
    WorkOptions.BlueCollarWorkers,
    WorkOptions.Media,
    WorkOptions.Agriculture,
    WorkOptions.DeliveryServices,
    WorkOptions.SocialServices,
    WorkOptions.Sports,
    WorkOptions.Insurance,
    WorkOptions.Construction,
    WorkOptions.RawMaterials,
    WorkOptions.Transport,
    WorkOptions.Tourism,
    WorkOptions.Services,
    WorkOptions.Ecology,
    WorkOptions.Energy,
    WorkOptions.Law,
    WorkOptions.TechnicalOrganization,
];

export const buildWorkOptions = (language: string): WorkOptionItem[] => {
    const isEn = language === 'en';

    return WORK_OPTIONS_VALUES.map((value) => ({
        value,
        label: isEn ? WORK_OPTION_LABELS[value].en : WORK_OPTION_LABELS[value].ru,
    }));
};
export enum WorkOptions {
    NotSet = 0,
    IT = 1,
    AdministrativeWork = 2,
    Banking = 3,
    Security = 4,
    Accounting = 5,
    CivilService = 6,
    Design = 7,
    DomesticStaff = 8,
    HousingAndUtilities = 9,
    Procurement = 10,
    Arts = 11,
    HR = 12,
    Consulting = 13,
    Marketing = 14,
    Medicine = 15,
    Science = 16,
    NonProfitOrganizations = 17,
    Sales = 18,
    Industry = 19,
    BlueCollarWorkers = 20,
    Media = 21,
    Agriculture = 22,
    DeliveryServices = 23,
    SocialServices = 24,
    Sports = 25,
    Insurance = 26,
    Construction = 27,
    RawMaterials = 28,
    Transport = 29,
    Tourism = 30,
    Services = 31,
    Ecology = 32,
    Energy = 33,
    Law = 34,
    TechnicalOrganization = 35,
}

export type WorkOptionItem = {
    label: string;
    value: WorkOptions;
};

type WorkOptionLabels = {
    ru: string;
    en: string;
};

/** Подписи из summary enum (ru / en). */
const WORK_OPTION_LABELS: Record<WorkOptions, WorkOptionLabels> = {
    [WorkOptions.NotSet]: { ru: 'Не задано', en: 'Not set' },
    [WorkOptions.IT]: {
        ru: 'IT, Интернет, связь, телеком',
        en: 'IT, Internet, Communications, Telecom',
    },
    [WorkOptions.AdministrativeWork]: {
        ru: 'Административная работа, секретариат, АХО',
        en: 'Administrative Work, Secretariat, Office Management',
    },
    [WorkOptions.Banking]: {
        ru: 'Банки, инвестиции, лизинг',
        en: 'Banking, Investments, Leasing',
    },
    [WorkOptions.Security]: {
        ru: 'Безопасность, службы охраны',
        en: 'Security, Guard Services',
    },
    [WorkOptions.Accounting]: {
        ru: 'Бухгалтерия, финансы, аудит',
        en: 'Accounting, Finance, Audit',
    },
    [WorkOptions.CivilService]: {
        ru: 'Государственная служба',
        en: 'Civil Service',
    },
    [WorkOptions.Design]: {
        ru: 'Дизайн, творчество',
        en: 'Design, Creative Arts',
    },
    [WorkOptions.DomesticStaff]: {
        ru: 'Домашний персонал',
        en: 'Domestic Staff',
    },
    [WorkOptions.HousingAndUtilities]: {
        ru: 'ЖКХ',
        en: 'Housing and Utilities',
    },
    [WorkOptions.Procurement]: {
        ru: 'Закупки, снабжение',
        en: 'Procurement, Supply',
    },
    [WorkOptions.Arts]: {
        ru: 'Искусство, культура, развлечения',
        en: 'Arts, Culture, Entertainment',
    },
    [WorkOptions.HR]: {
        ru: 'Кадры, управление персоналом',
        en: 'HR, Personnel Management',
    },
    [WorkOptions.Consulting]: {
        ru: 'Консалтинг, стратегическое развитие',
        en: 'Consulting, Strategic Development',
    },
    [WorkOptions.Marketing]: {
        ru: 'Маркетинг, реклама, PR',
        en: 'Marketing, Advertising, PR',
    },
    [WorkOptions.Medicine]: {
        ru: 'Медицина, фармацевтика, ветеринария',
        en: 'Medicine, Pharmaceuticals, Veterinary',
    },
    [WorkOptions.Science]: {
        ru: 'Наука, образование, повышение квалификации',
        en: 'Science, Education, Professional Development',
    },
    [WorkOptions.NonProfitOrganizations]: {
        ru: 'Некоммерческие организации, волонтерство',
        en: 'Non-Profit Organizations, Volunteering',
    },
    [WorkOptions.Sales]: { ru: 'Продажи', en: 'Sales' },
    [WorkOptions.Industry]: {
        ru: 'Промышленность, производство',
        en: 'Industry, Manufacturing',
    },
    [WorkOptions.BlueCollarWorkers]: {
        ru: 'Рабочий персонал',
        en: 'Blue-Collar Workers',
    },
    [WorkOptions.Media]: {
        ru: 'СМИ, издательства',
        en: 'Media, Publishing',
    },
    [WorkOptions.Agriculture]: {
        ru: 'Сельское хозяйство',
        en: 'Agriculture',
    },
    [WorkOptions.DeliveryServices]: {
        ru: 'Службы доставки',
        en: 'Delivery Services',
    },
    [WorkOptions.SocialServices]: {
        ru: 'Социальные службы',
        en: 'Social Services',
    },
    [WorkOptions.Sports]: {
        ru: 'Спорт, фитнес, салоны красоты, SPA',
        en: 'Sports, Fitness, Beauty Salons, SPA',
    },
    [WorkOptions.Insurance]: { ru: 'Страхование', en: 'Insurance' },
    [WorkOptions.Construction]: {
        ru: 'Строительство, проектирование, недвижимость',
        en: 'Construction, Design, Real Estate',
    },
    [WorkOptions.RawMaterials]: { ru: 'Сырье', en: 'Raw Materials' },
    [WorkOptions.Transport]: {
        ru: 'Транспорт, логистика, ВЭД',
        en: 'Transport, Logistics, Foreign Trade',
    },
    [WorkOptions.Tourism]: {
        ru: 'Туризм, гостиницы, общественное питание',
        en: 'Tourism, Hotels, Catering',
    },
    [WorkOptions.Services]: {
        ru: 'Услуги, ремонт, сервисное обслуживание',
        en: 'Services, Repair, Maintenance',
    },
    [WorkOptions.Ecology]: { ru: 'Экология', en: 'Ecology' },
    [WorkOptions.Energy]: { ru: 'Энергетика', en: 'Energy' },
    [WorkOptions.Law]: { ru: 'Юриспруденция', en: 'Law, Legal Services' },
    [WorkOptions.TechnicalOrganization]: {
        ru: 'Техническая организация',
        en: 'Technical organization',
    },
};

/** Значения enum в порядке объявления (без NotSet — для селекта). */
const WORK_OPTIONS_VALUES: WorkOptions[] = [
    WorkOptions.IT,
    WorkOptions.AdministrativeWork,
    WorkOptions.Banking,
    WorkOptions.Security,
    WorkOptions.Accounting,
    WorkOptions.CivilService,
    WorkOptions.Design,
    WorkOptions.DomesticStaff,
    WorkOptions.HousingAndUtilities,
    WorkOptions.Procurement,
    WorkOptions.Arts,
    WorkOptions.HR,
    WorkOptions.Consulting,
    WorkOptions.Marketing,
    WorkOptions.Medicine,
    WorkOptions.Science,
    WorkOptions.NonProfitOrganizations,
    WorkOptions.Sales,
    WorkOptions.Industry,
    WorkOptions.BlueCollarWorkers,
    WorkOptions.Media,
    WorkOptions.Agriculture,
    WorkOptions.DeliveryServices,
    WorkOptions.SocialServices,
    WorkOptions.Sports,
    WorkOptions.Insurance,
    WorkOptions.Construction,
    WorkOptions.RawMaterials,
    WorkOptions.Transport,
    WorkOptions.Tourism,
    WorkOptions.Services,
    WorkOptions.Ecology,
    WorkOptions.Energy,
    WorkOptions.Law,
    WorkOptions.TechnicalOrganization,
];

export const buildWorkOptions = (language: string): WorkOptionItem[] => {
    const isEn = language === 'en';

    return WORK_OPTIONS_VALUES.map((value) => ({
        value,
        label: isEn ? WORK_OPTION_LABELS[value].en : WORK_OPTION_LABELS[value].ru,
    }));
};
